import {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {MemberLookupResult} from '../core/customerLookup'

type Props = {result: MemberLookupResult | null; onClose: () => void}

const formatDate = (date: string) => {
  if (!date || date === '-') return '-'

  const parsedDate = new Date(`${date.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(parsedDate.getTime())) return date

  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(parsedDate)
}

const getInitials = (name: string) => {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')

  return initials || 'M'
}

export function ReservationSuccessModal({result, onClose}: Props) {
  const memberFound = result?.kind === 'found'
  const isActive = memberFound && result.member.status.toLowerCase() === 'active'
  const [profileImageError, setProfileImageError] = useState(false)

  useEffect(() => {
    setProfileImageError(false)
  }, [result])

  return (
    <Modal
      show={Boolean(result)}
      onHide={onClose}
      centered
      size={memberFound ? 'lg' : undefined}
      contentClassName='border-0 rounded-4 shadow-lg'
      aria-labelledby='gym-success-title'
    >
      <Modal.Header closeButton>
        <Modal.Title id='gym-success-title'>Hasil Pengecekan Member</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          className={`gym-member-status d-flex align-items-center gap-4 mb-6 ${
            memberFound ? '' : 'gym-member-status-not-found'
          }`}
        >
          <div
            className={`gym-member-status-icon d-flex align-items-center justify-content-center ${
              memberFound ? '' : 'gym-member-status-icon-not-found'
            }`}
          >
            <i
              className={`bi ${memberFound ? 'bi-person-check' : 'bi-person-x'} fs-1`}
              aria-hidden='true'
            />
          </div>
          <div>
            {memberFound ? (
              <>
                <span
                  className={`badge ${
                    isActive ? 'badge-light-success' : 'badge-light-danger'
                  } fw-bold mb-2`}
                >
                  {result.member.status.toUpperCase()}
                </span>
                <h3 className='fs-4 fw-bold mb-1'>Data member ditemukan</h3>
                <p className='text-gray-600 mb-0'>Status berasal dari data customer terbaru.</p>
              </>
            ) : (
              <>
                <span className='badge badge-light-danger fw-bold mb-2'>TIDAK DITEMUKAN</span>
                <h3 className='fs-4 fw-bold mb-1'>Data member tidak ditemukan</h3>
                <p className='text-gray-600 mb-0'>Periksa kembali nomor HP dan email Anda.</p>
              </>
            )}
          </div>
        </div>
        {memberFound && (
          <div className='gym-member-profile mb-5 text-break'>
            <div className='gym-member-identity d-flex align-items-center gap-4 p-5'>
              <div className='gym-member-avatar d-flex align-items-center justify-content-center'>
                {result.member.profilePicture && !profileImageError ? (
                  <img
                    src={result.member.profilePicture}
                    alt={`Foto profil ${result.member.fullName}`}
                    onError={() => setProfileImageError(true)}
                  />
                ) : (
                  <span aria-hidden='true'>{getInitials(result.member.fullName)}</span>
                )}
              </div>
              <div className='min-w-0'>
                <h3 className='fs-3 fw-bolder mb-1'>{result.member.fullName}</h3>
                <p className='text-gray-600 fw-semibold mb-0'>{result.member.customerType}</p>
              </div>
            </div>

            <div className='gym-member-profile-section px-5 pt-5'>
              <h4 className='gym-member-section-title'>Informasi Member</h4>
              <dl className='row gx-6 gy-5 mb-0'>
                {[
                  ['Nomor HP', result.member.phoneNumber],
                  ['Email', result.member.email],
                  ['Jenis Kelamin', result.member.gender],
                  ['Tanggal Lahir', formatDate(result.member.dateOfBirth)],
                ].map(([label, value]) => (
                  <div className='col-12 col-sm-6' key={label}>
                    <dt className='text-gray-600 fs-8 fw-semibold mb-1'>{label}</dt>
                    <dd className='mb-0 fw-bold'>{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className='gym-member-profile-section p-5'>
              <h4 className='gym-member-section-title'>Informasi Outlet</h4>
              <dl className='row gx-6 gy-5 mb-0'>
                {[
                  ['Outlet Terdaftar', result.member.registeredOutlet],
                  ['Outlet Pengecekan', result.outletName],
                  ['Kota', result.member.city],
                  ['Provinsi', result.member.province],
                  ['Alamat', result.member.address],
                ].map(([label, value], index) => (
                  <div className={index === 4 ? 'col-12' : 'col-12 col-sm-6'} key={label}>
                    <dt className='text-gray-600 fs-8 fw-semibold mb-1'>{label}</dt>
                    <dd className='mb-0 fw-bold'>{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        )}
        {result?.kind === 'not-found' && (
          <dl className='gym-member-details mb-0 text-break'>
            {[
              ['Nomor HP', result.phone],
              ['Email', result.email],
            ].map(([label, value]) => (
              <div className='d-flex justify-content-between gap-5 py-4 border-bottom' key={label}>
                <dt className='text-gray-600 fw-semibold'>{label}</dt>
                <dd className='mb-0 text-end fw-bold'>{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </Modal.Body>
    </Modal>
  )
}
